import { useCallback, useEffect, useRef, useState } from "react";
import type { RefObject } from "react";
import { postSessionControl, sessionControlOrigin } from "@/components/runs/sessionControl";
import { activeAppFrame } from "./activeAppFrame";
import { parseDiscoverySelection, parsePreviewParam } from "./discoveryUrl";

/** `/?view=discover` is the Grid. Single (preview) is explicit in the URL. */
export const DISCOVERY_PREVIEW_STYLE = "preview";

function readView(): "play" | "list" {
  const params = new URLSearchParams(window.location.search);
  if (parsePreviewParam(params.get("c")) || parseDiscoverySelection(params.get("app"))) return "play";
  return params.get("style") === DISCOVERY_PREVIEW_STYLE ? "play" : "list";
}

function control(frame: HTMLIFrameElement, action: "pause" | "resume") {
  const target = frame.contentWindow;
  const origin = sessionControlOrigin(frame.src);
  if (!target || !origin) return Promise.resolve(false);
  return new Promise<boolean>((resolve) => {
    let requestId: string;
    const finish = (ok: boolean) => {
      window.removeEventListener("message", receive);
      window.clearTimeout(timer);
      resolve(ok);
    };
    const receive = (event: MessageEvent) => {
      const data = event.data;
      if (event.source === target && event.origin === origin && data?.type === "ato.session-control.v1.ack" &&
          data.scope === "surface" && data.action === action && data.request_id === requestId) finish(data.ok === true);
    };
    const timer = window.setTimeout(() => finish(false), 1500);
    window.addEventListener("message", receive);
    requestId = postSessionControl(target, origin, action, "surface");
  });
}

/** Keep the same iframe and trial controller alive, with acknowledged audio suspension. */
export function useDiscoveryView(
  stage: RefObject<HTMLElement>,
  options: { skipGridGuard?: boolean } = {},
) {
  const [view, setView] = useState<"play" | "list">(readView);
  useEffect(() => {
    // A selected app already identifies Single; only the unselected preview
    // needs its own marker. Keep every other query field and history state.
    const url = new URL(window.location.href);
    if (url.searchParams.has("app") || url.searchParams.has("c")) return;
    if (view === "play") url.searchParams.set("style", DISCOVERY_PREVIEW_STYLE);
    else url.searchParams.delete("style");
    const next = url.pathname + url.search + url.hash;
    if (next !== window.location.pathname + window.location.search + window.location.hash)
      history.replaceState(history.state, "", next);
  }, [view]);
  const [switching, setSwitching] = useState(false);
  const [viewError, setViewError] = useState<string | null>(null);
  const suspended = useRef<HTMLIFrameElement | null>(null);
  const lock = useRef(false);
  const allowUncooperative = useRef(false);
  // Static documents have no session-control bridge. They were retained
  // without a pause ack, so there is nothing to resume or to warn about.
  const passive = useRef(new WeakSet<HTMLIFrameElement>());
  const changeView = useCallback(async (
    next: "play" | "list",
    options?: { allowUncooperative?: boolean },
  ) => {
    if (next === "list")
      allowUncooperative.current = Boolean(options?.allowUncooperative);
    if (next === view || lock.current) return;
    lock.current = true;
    setSwitching(true);
    setViewError(null);
    try {
      const frame = activeAppFrame(stage.current);
      if (next === "list" && frame) {
        if (!await control(frame, "pause")) {
          // A static public document has no session-control bridge. Keep it
          // mounted and hidden (passive retention) instead of discarding it;
          // the caller opts in explicitly.
          if (options?.allowUncooperative) passive.current.add(frame);
          else {
            // A failed or late pause may still have reached the app. Undo it.
            void control(frame, "resume");
            setViewError("This app couldn't pause its audio. Stop the app before switching to Grid.");
            return;
          }
        }
        suspended.current = frame;
      } else if (next === "play" && suspended.current?.isConnected) {
        if (!passive.current.has(suspended.current) && !await control(suspended.current, "resume")) {
          setView("play");
          setViewError("Couldn't confirm audio resumed. The app is visible; try its audio controls.");
          return;
        }
        // Keep the frame recorded until Grid's observer has disconnected.
        // Clearing it here allows a pending mutation to pause it again.
      }
      if (next === "list") {
        const bounds = stage.current?.getBoundingClientRect();
        if (bounds && stage.current) {
          stage.current.style.setProperty("--stage-width", `${bounds.width}px`);
          stage.current.style.setProperty("--stage-height", `${bounds.height}px`);
        }
        const active = document.activeElement;
        if (active instanceof HTMLElement && stage.current?.contains(active)) active.blur();
      }
      setView(next);
      return true;
    } finally { lock.current = false; setSwitching(false); }
  }, [stage, view, options.skipGridGuard]);
  useEffect(() => {
    if (view !== "list") { suspended.current = null; return; }
    // A details-modal launch is about to switch to Play. There is no Grid
    // suspension to perform, and pausing a just-mounted frame would fail for
    // apps that do not implement session control.
    if (options.skipGridGuard) return;
    if (!stage.current) return;
    const root = stage.current;
    root.setAttribute("inert", "");
    const guard = () => {
      const frame = activeAppFrame(root);
      if (!frame || frame === suspended.current) return;
      suspended.current = frame;
      void control(frame, "pause").then((ok) => {
        if (!ok && frame.isConnected) {
          if (allowUncooperative.current) { passive.current.add(frame); return; }
          void control(frame, "resume");
          setView("play");
          setViewError("This app couldn't pause its audio. Stop the app before switching to Grid.");
        }
      });
    };
    const observer = new MutationObserver(guard);
    observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ["hidden"] });
    root.addEventListener("load", guard, true);
    guard();
    return () => { root.removeAttribute("inert"); observer.disconnect(); root.removeEventListener("load", guard, true); };
  }, [stage, view]);
  return { view, changeView, switching, viewError };
}
